/**
 * The little icon browsers show in the tab, bookmarks and on a phone home
 * screen.
 *
 * Kept separate from the logo because it is a different picture, not a
 * smaller one. A wordmark scaled to 16px is an illegible smear — at that size
 * only a single letter or a simple mark survives, so brands need somewhere to
 * put the mark-only version.
 *
 * The preview is shown at the real sizes rather than one large sample. "Looks
 * fine at 200px" is exactly the mistake this field exists to prevent, and the
 * only honest way to show that is to render it at 16 and 32.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { ImageDropUploader } from 'dashboard/components/common';

export default function Favicon({ brandData, fetchData }) {
  const brandId = brandData?._id;
  const [src, setSrc] = useState('');

  useEffect(() => {
    if (brandData) setSrc(brandData.favicon_upload_path || '');
  }, [brandData]);

  const save = (url) => {
    if (!brandId) return;
    setSrc(url);
    const fd = new FormData();
    fd.append('data', JSON.stringify({ brand_id: brandId, favicon_upload_path: url }));
    API.updateBrandData(fd)
      .then(() => { toast.success('Tab icon saved'); fetchData && fetchData(); })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  return (
    <Card sx={{ p: '30px', py: '28px' }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Typography variant='h6' fontWeight='bold' mb='4px'>Tab icon</Typography>
        <Typography variant='body2' color='text.secondary' mb='16px'>
          Shown in the browser tab, in bookmarks, and on a phone home screen. Use a square
          image — a simple mark or a single letter, not your full logo, which turns to mush at
          this size.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing='20px' alignItems='flex-start'>
          <Box flex={1} width='100%'>
            <ImageDropUploader
              brandId={brandId}
              currentSrc={src}
              onUploaded={save}
            />
            <Typography variant='caption' color='text.secondary' display='block' mt='8px'>
              PNG or SVG, square, at least 64×64.
            </Typography>
          </Box>

          {src && (
            <Box>
              <Typography variant='caption' color='text.secondary' display='block' mb='8px'>
                How it will actually look
              </Typography>
              <Stack direction='row' spacing='16px' alignItems='flex-end'>
                {[16, 32, 48].map((size) => (
                  <Box key={size} textAlign='center'>
                    <Box
                      component='img'
                      src={src}
                      alt=''
                      sx={{
                        width: size, height: size, objectFit: 'contain',
                        display: 'block', mx: 'auto',
                        border: '1px solid', borderColor: 'divider', borderRadius: '3px',
                      }}
                    />
                    <Typography variant='caption' color='text.secondary'>{size}px</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
